import React, { forwardRef } from 'react';

import { Box, Button, FormHelperText } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { FieldError } from 'react-hook-form';
import SingleDeliveryTime from './SingleDeliveryTime';

import { DeliveryTime } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { dayTypeOptions } from 'views/Store/Tabs/OpeningHours/utils';
import { getDefaultDeliveryTime } from '../utils';

interface Props {
	value?: DeliveryTime[];
	onChange?: (value: DeliveryTime[]) => void;
	error?: FieldError;
	deleteDeliveryPeriod: () => void;
}

const DeliveryTimes = forwardRef((props: Props, ref) => {
	const { value = [], onChange, error, deleteDeliveryPeriod } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const addedDayTypes = value.map((time) => time.dayType) ?? [];

	const isLastDeliverySlot = value.length === 1 && value[0].slots.length === 1;

	const handleDeliveryTimeChange = (deliveryTime: DeliveryTime, index: number) => {
		const newDeliveryTimes = value.map((time, i) => (i === index ? deliveryTime : time));
		onChange?.(newDeliveryTimes);
	};

	const handleDeliveryTimeDelete = (slotIndex: number, timeIndex: number) => {
		isLastDeliverySlot && deleteDeliveryPeriod();

		const newDeliveryTimes =
			value[timeIndex].slots.length > 1
				? value.map((time, i) =>
						i === timeIndex
							? { ...time, slots: time.slots.filter((_, i) => i !== slotIndex) }
							: time,
				  )
				: value.filter((_, i) => i !== timeIndex);
		onChange?.(newDeliveryTimes);
	};

	const handleAddNewDeliveryTime = () => {
		const newDayType = dayTypeOptions.find((dayType) => !addedDayTypes.includes(dayType));
		if (newDayType) {
			onChange?.([...value, getDefaultDeliveryTime(newDayType)]);
		}
	};

	return (
		<Box>
			{error?.type === 'required' && (
				<FormHelperText error data-error="true">
					{error.message}
				</FormHelperText>
			)}
			{value.map((time, index) => (
				<Box key={index}>
					<SingleDeliveryTime
						value={time}
						addedDayTypes={addedDayTypes}
						onChange={(value) => handleDeliveryTimeChange(value, index)}
						onDelete={(slotIndex) => handleDeliveryTimeDelete(slotIndex, index)}
					/>
				</Box>
			))}
			{addedDayTypes.length < dayTypeOptions.length && (
				<Box mt={3}>
					<Button
						variant="outlined"
						color="primary"
						className={classes.button}
						onClick={handleAddNewDeliveryTime}
					>{`+ ${t('delivery.addNewRow', 'Add new row')}`}</Button>
				</Box>
			)}
		</Box>
	);
});

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		button: {
			minHeight: 32,
			maxHeight: 32,
		},
	}),
);

export default DeliveryTimes;
